import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Download, ChevronDown, Copy, FileText, FileSpreadsheet, Printer } from "lucide-react";

interface ExportDropdownProps {
  onCopy?: (e: React.MouseEvent) => void;
  onCSV?: (e: React.MouseEvent) => void;
  onExcel?: (e: React.MouseEvent) => void;
  onPDF?: (e: React.MouseEvent) => void;
  onPrint?: (e: React.MouseEvent) => void;
}

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #ffffff;
  color: #171717;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;

  &:hover {
    background: #f8f9fa;
    border-color: rgba(23, 23, 23, 0.2);
    transform: translateY(-1px);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  width: 160px;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  z-index: 100;
  overflow: hidden;
  padding: 4px 0;
`;

const DropdownItem = styled.button<{ $color?: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: transparent;
  border: none;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #2b2b2b;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;

  &:hover {
    background: rgba(201, 77, 170, 0.08);
    color: ${(props) => props.$color || "#c94daa"};
  }

  svg {
    color: ${(props) => props.$color || "inherit"};
  }
`;

export const ExportDropdown: React.FC<ExportDropdownProps> = ({
  onCopy,
  onCSV,
  onExcel,
  onPDF,
  onPrint,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAction = (e: React.MouseEvent, action?: (e: React.MouseEvent) => void) => {
    setIsOpen(false);
    if (action) action(e);
  };

  return (
    <DropdownContainer ref={containerRef}>
      <TriggerButton type="button" onClick={() => setIsOpen(!isOpen)}>
        <Download size={15} color="#c94daa" />
        Download
        <ChevronDown size={14} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />
      </TriggerButton>

      {isOpen && (
        <DropdownMenu>
          {onCopy && (
            <DropdownItem type="button" $color="#8e4e8f" onClick={(e) => handleAction(e, onCopy)}>
              <Copy size={14} />
              Copy
            </DropdownItem>
          )}
          {onCSV && (
            <DropdownItem type="button" $color="#35b7c8" onClick={(e) => handleAction(e, onCSV)}>
              <FileText size={14} />
              CSV
            </DropdownItem>
          )}
          {onExcel && (
            <DropdownItem type="button" $color="#31a66a" onClick={(e) => handleAction(e, onExcel)}>
              <FileSpreadsheet size={14} />
              Excel
            </DropdownItem>
          )}
          {onPDF && (
            <DropdownItem type="button" $color="#e44862" onClick={(e) => handleAction(e, onPDF)}>
              <FileText size={14} />
              PDF
            </DropdownItem>
          )}
          {onPrint && (
            <DropdownItem type="button" $color="#f09a3e" onClick={(e) => handleAction(e, onPrint)}>
              <Printer size={14} />
              Print
            </DropdownItem>
          )}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

export default ExportDropdown;
